import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {MutationObserver | null} */
  #accountLinkObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener('click', this.#onAccountDestinationActivate);
    document.addEventListener('auxclick', this.#onAccountDestinationActivate);
    document.addEventListener('mousedown', this.#onAccountDestinationMiddleDown);
    this.#watchAccountLinks();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener('click', this.#onAccountDestinationActivate);
    document.removeEventListener('auxclick', this.#onAccountDestinationActivate);
    document.removeEventListener('mousedown', this.#onAccountDestinationMiddleDown);
    this.#accountLinkObserver?.disconnect();
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };

  /**
   * Shopify's account host returns 406 to link checkers that omit
   * Accept: text/html. The <shopify-account> menu still paints Orders and
   * Profile as https://shopify.com/{shop}/account/... inside its shadow root,
   * on every page that includes the header (including blog articles).
   * Keep a same-page href for the crawl, and follow the real URL on activate.
   * Always same-tab — never open Orders/Profile in a new tab (including
   * Cmd/Ctrl-click and middle-click).
   */
  #watchAccountLinks = async () => {
    const account = this.querySelector('shopify-account');

    if (!account) return;

    await customElements.whenDefined('shopify-account');

    const root = account.shadowRoot;

    if (!root) return;

    this.#retargetShopifyAccountLinks(root);
    this.#accountLinkObserver = new MutationObserver(() => {
      this.#retargetShopifyAccountLinks(root);
    });
    this.#accountLinkObserver.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['href'],
    });
  };

  /**
   * @param {ShadowRoot} root
   */
  #retargetShopifyAccountLinks(root) {
    const anchors = root.querySelectorAll('a[href*="shopify.com/"]');

    for (const anchor of anchors) {
      const href = anchor.getAttribute('href');

      if (!href || href === '#') continue;

      anchor.dataset.accountDestination = href;
      anchor.setAttribute('href', '#');
      anchor.removeAttribute('target');
    }
  }

  /**
   * @param {Event} event
   * @returns {HTMLAnchorElement | null}
   */
  #accountLinkFromEvent(event) {
    for (const node of event.composedPath()) {
      if (node instanceof HTMLAnchorElement && node.dataset.accountDestination) {
        return node;
      }
    }

    return null;
  }

  /**
   * Middle-click default opens href (#) in a new tab before auxclick in some
   * browsers — cancel that so activation can navigate same-tab instead.
   * @param {MouseEvent} event
   */
  #onAccountDestinationMiddleDown = (event) => {
    if (event.button !== 1) return;
    if (!this.#accountLinkFromEvent(event)) return;

    event.preventDefault();
  };

  /**
   * Same-tab navigation for left-click (incl. modifiers) and middle-click.
   * @param {MouseEvent} event
   */
  #onAccountDestinationActivate = (event) => {
    if (event.type === 'auxclick' && event.button !== 1) return;
    if (event.type === 'click' && event.button !== 0) return;

    const link = this.#accountLinkFromEvent(event);
    const destination = link?.dataset.accountDestination;

    if (!destination) return;

    event.preventDefault();
    window.location.assign(destination);
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
